import { useBalance } from '../trade/hooks/use-balance';
import { useTradeSettings } from '../trade/hooks/use-trade-settings';
import { TradeConfirm } from '../trade/trade-confirm';
import { TreadInput } from '../trade/trade-input';
import { TradeSettings } from '../trade/trade-settings';
import { shouldRefetchTradeQuery } from '../trade/trade-success-queries';
import { Button } from '../ui/button';
import { InteractCloseIcon } from '@/client/assets';
import { useAuth } from '@/client/hooks/use-auth';
import { api } from '@/client/utils/api';
import { useExpired } from '@/client/utils/clock';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type SellContextProps = {
  marketAddress: string;
  optionAddress: string;
  setOpen?: (open: boolean) => void;
};

export const SellContext = ({ marketAddress, optionAddress, setOpen }: SellContextProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const type = 'sell';
  const { data } = useAuth();
  const uid = useMemo(() => data?.uid ?? '', [data?.uid]);
  const market = useMarketDetail(api, { marketAddress });
  const [amount, setAmount] = useState<bigint | undefined>();
  const [successMessage, setSuccessMessage] = useState<string>();
  const balance = useBalance({ type, marketAddress, optionAddress, uid });
  const isExpire = useExpired(Number(market.data?.deadline));
  const currentOption = useMemo(
    () => market.data?.optionList.find(item => item.optionAddress.toLowerCase() === optionAddress.toLowerCase()),
    [market.data?.optionList, optionAddress]
  );
  const { deadlineMinutes, slippage, isAutoSlippage } = useTradeSettings();

  useEffect(() => {
    setSuccessMessage(undefined);
  }, [amount, marketAddress, optionAddress]);

  const handleTradeSuccess = async () => {
    // Refetch the mounted trade queries only; other matches are marked stale and
    // refetch on mount. A read in flight restarts, so the refresh reads the chain
    // after the trade (this sheet has no fallback refresh).
    await queryClient.invalidateQueries({
      predicate: query => shouldRefetchTradeQuery(query.queryKey, { uid, marketAddress }),
      refetchType: 'active',
    });
    setAmount(undefined);
    setSuccessMessage(t('sell-success'));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2>
          {t('sell')} {currentOption?.description ?? currentOption?.optionName}
        </h2>
        <Button
          size="icon"
          variant="ghost"
          className="transition-all duration-200 hover:scale-90 focus:scale-80"
          onClick={() => setOpen?.(false)}
        >
          <InteractCloseIcon className="!size-6 fill-foreground/40" />
        </Button>
      </div>
      <div className="w-full !mt-2">
        <div className="text-foreground/40 flex items-center justify-between pt-3 pb-2">
          <span className="text-sm leading-3">{t('shares')}</span>
          <TradeSettings />
        </div>
        <TreadInput
          type="sell"
          maxAmount={balance.data}
          value={amount}
          onValueChange={setAmount}
          isLoading={balance.isLoading}
          decimal={balance.decimal}
        />
      </div>
      {isExpire && <div className="text-center text-sm text-destructive mb-4">{t('market-closed-awaiting-resolution')}</div>}
      {successMessage && (
        <div className="rounded-3 border border-beneficial/30 bg-beneficial/10 px-4 py-3 text-sm font-medium text-beneficial">
          {successMessage}
        </div>
      )}
      <TradeConfirm
        type={type}
        marketAddress={marketAddress}
        optionAddress={optionAddress}
        amount={amount}
        deadlineMinutes={deadlineMinutes}
        slippage={slippage}
        isAutoSlippage={isAutoSlippage}
        disabled={!balance.data || Number(amount) > balance.data || isExpire || balance.isLoading}
        uid={uid}
        onSuccess={handleTradeSuccess}
      />
    </div>
  );
};
